import { type Locale, localizePath } from "@lib/i18n/locale";
import { getCategoryPath, getLearningCenterPath } from "@lib/routing";

export interface HubLocation {
  category: string | null;
  page: number;
}

export type HubRequest =
  | ({ kind: "page" } & HubLocation)
  | { kind: "redirect"; location: HubLocation }
  | { kind: "not-found" };

const PAGE_SEGMENT: Record<Locale, string> = {
  de: "seite",
  en: "page",
  immo: "seite",
};

// The slugs of the category pages this route replaces, mapped to the category that took
// them over; `null` sends the visitor to the overview.
const LEGACY_SLUGS = new Map<string, string | null>([
  ["immobilienfinanzierung", "finanzierung"],
  ["hausbesichtigung", "besichtigung"],
  ["kauf-abschließen", "kaufabschluss"],
  ["kauf-abschliessen", "kaufabschluss"],
  ["eigentümer-sein", "eigentuemer-sein"],
  ["suchen", null],
  ["mortgage-basics", "financing"],
  ["equity-gap", "financing"],
  ["refinancing", "homeownership"],
  ["home-buying", null],
  ["search", null],
]);

const NOT_FOUND: HubRequest = { kind: "not-found" };

const decode = (segment: string) => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const toCurrentSlug = (slug: string) => {
  const lower = slug.toLowerCase();
  const current = LEGACY_SLUGS.get(lower);

  return current === undefined ? lower : current;
};

export const SEARCH_PARAMS: Record<Locale, string> = {
  de: "suche",
  en: "search",
  immo: "suche",
};

export const toSearchQuery = (locale: Locale, query: string) =>
  query ? `?${new URLSearchParams({ [SEARCH_PARAMS[locale]]: query })}` : "";

export const GRID_ANCHORS: Record<Locale, string> = {
  de: "artikel",
  en: "articles",
  immo: "artikel",
};

export const getHubPath = (locale: Locale, { category, page }: HubLocation) => {
  const base = category
    ? getCategoryPath(locale, category)
    : getLearningCenterPath(locale);

  return page > 1 ? `${base}/${PAGE_SEGMENT[locale]}/${page}` : base;
};

/** A link into the article list, which is where the visitor was looking when they chose it. */
export const toGridHref = (locale: Locale, location: HubLocation) =>
  `${localizePath(locale, getHubPath(locale, location))}#${GRID_ANCHORS[locale]}`;

export const parseHubSegments = (
  locale: Locale,
  segments: string[] = [],
): HubRequest => {
  const parts = segments.map(decode);
  const pageSegment = PAGE_SEGMENT[locale];
  const categoryPart =
    parts[0] !== undefined && parts[0] !== pageSegment ? parts[0] : null;
  const pageParts = parts.slice(categoryPart === null ? 0 : 1);

  if (
    pageParts.length &&
    (pageParts.length !== 2 ||
      pageParts[0] !== pageSegment ||
      !/^\d+$/.test(pageParts[1]!))
  ) {
    return NOT_FOUND;
  }

  const page = pageParts.length ? Number(pageParts[1]) : 1;

  if (page < 1) {
    return NOT_FOUND;
  }

  const location = {
    category: categoryPart === null ? null : toCurrentSlug(categoryPart),
    page,
  };
  const requested = [getLearningCenterPath(locale), ...parts].join("/");

  return getHubPath(locale, location) === requested
    ? { kind: "page", ...location }
    : { kind: "redirect", location };
};
